import { ChartFilters, ChartSwitches } from 'interfaces/store/chartFilters';
import {
  ChartDataMap,
  ChartsARRRangeLabels,
  ChartsAvgDealSizeLabels,
  CompanyMetrics
} from 'utils/helpers/chartsDataHelpers';
import { mapChartData } from 'utils/helpers/mapChartsData';

export interface Dataset {
  label: string;
  labelComponent?: React.ReactNode;
  data: (number | null | undefined)[];
  backgroundColor: string;
  pointStyle?: 'rectRot' | 'circle' | 'rect';
}

interface ProcessedChartData {
  labels: string[];
  datasets: Dataset[];
}

export type HasData = (
  dataset: Dataset,
  labels: string[],
  isRuleof40?: boolean
) => boolean;

export interface ChartSectionProps {
  data: Dataset;
  datasets: Dataset[];
  chart: React.ReactNode;
  isRuleof40?: boolean;
  hasData: HasData;
  labels: string[];
}

export const getDataCharts = (
  quartileMedian: boolean,
  chartData: ChartDataMap[],
  companyMetrics: CompanyMetrics,
  chartFilters: ChartFilters,
  chartSwitches: ChartSwitches
) => {
  const { datasets, datasetsStacked } = mapChartData(
    chartData,
    companyMetrics,
    chartFilters,
    chartSwitches
  );
  //checks if yourcompany array exists
  const yourCompanyVerifyDatasets = (data: any[], quartileMedian: boolean) => {
    const swappedData = [data[0], data[1], data[2] ?? null];
    return [swappedData[quartileMedian ? 1 : 0], swappedData[2]].filter(
      Boolean
    );
  };

  // const yourCompanyVerifyLineDatasets = (
  //   data: any[],
  //   quartileMedian: boolean
  // ) => {
  //   const swappedData = [data[0], data[1], data[2] ?? null, data[3] ?? null];

  //   const youCompanyArray = swappedData[2]
  //     ? [swappedData[2], swappedData[3]].filter(Boolean)
  //     : [];

  //   return [...swappedData[quartileMedian ? 1 : 0], ...youCompanyArray];
  // };

  //checks if yourcompany array exists for stacked bars
  const yourCompanyVerifyStackedDatasets = (
    data: any[][],
    quartileMedian: boolean
  ) => {
    const swappedData = [data[0], data[1], data[2]].filter(Boolean);

    const youCompanyArray = swappedData[2]
      ? swappedData[2].filter(Boolean)
      : [];

    return [...swappedData[quartileMedian ? 1 : 0], ...youCompanyArray];
  };
  //remove null data
  const filterNullValues = (
    labels: string[],
    datasets: Dataset[]
  ): ProcessedChartData => {
    const filteredLabels: string[] = [];
    const filteredDatasets: Dataset[] = datasets.map(dataset => ({
      ...dataset,
      data: [] as (number | null)[]
    }));

    labels.forEach((label, index) => {
      const isNullInAll = datasets.every(
        dataset => dataset?.data?.[index] === null
      );

      if (!isNullInAll) {
        filteredLabels.push(label);
        datasets.forEach((dataset, datasetIndex) => {
          filteredDatasets[datasetIndex]?.data.push(dataset?.data?.[index]);
        });
      }
    });

    return { labels, datasets: filteredDatasets };
  };

  const processChartData = (
    labels: string[],
    datasets: Dataset[]
  ): ProcessedChartData => {
    return filterNullValues(labels, datasets);
  };

  const labels = chartSwitches.xAxisSwitch
    ? ChartsARRRangeLabels
    : ChartsAvgDealSizeLabels;

  const barChartData = [
    {
      title: 'YoY ARR Growth',
      ...processChartData(
        ChartsARRRangeLabels,
        yourCompanyVerifyDatasets(datasets.YoYARRGrowth, quartileMedian)
      )
    },
    {
      title: 'Gross Dollar Retention',
      ...processChartData(
        labels,
        yourCompanyVerifyDatasets(datasets.grossDollarRetention, quartileMedian)
      )
    },

    {
      title: 'Net Magic Number',
      ...processChartData(
        labels,
        yourCompanyVerifyDatasets(datasets.netMagicNumber, quartileMedian)
      )
    },

    {
      title: 'Gross Margin',
      ...processChartData(
        ChartsARRRangeLabels,
        yourCompanyVerifyDatasets(datasets.grossMargin, quartileMedian)
      )
    },
    {
      title: 'Burn Multiple',
      ...processChartData(
        ChartsARRRangeLabels,
        yourCompanyVerifyDatasets(datasets.burnMultiple, quartileMedian)
      )
    },
    {
      title: 'ARR per FTE',
      ...processChartData(
        ChartsARRRangeLabels,
        yourCompanyVerifyDatasets(datasets.ARRperFTE, quartileMedian)
      )
    },
    {
      title: 'OpEx as a % of Revenue',
      ...processChartData(ChartsARRRangeLabels, [
        ...yourCompanyVerifyStackedDatasets(datasetsStacked.OpEx, true)
      ])
    },
    {
      title: 'FCF Margin',
      ...processChartData(
        ChartsARRRangeLabels,
        yourCompanyVerifyDatasets(datasets.FCFMargin, quartileMedian)
      )
    },
    {
      title: 'Quick Ratio',
      ...processChartData(
        labels,
        yourCompanyVerifyDatasets(datasets.quickRatio, quartileMedian)
      )
    },
    {
      title: 'Headcount Distribution',
      ...processChartData(ChartsARRRangeLabels, [
        ...yourCompanyVerifyStackedDatasets(
          datasetsStacked.headcountDistribution,
          false
        )
      ])
    },
    {
      title: 'Gross New ARR Composition',
      ...processChartData(
        labels,
        yourCompanyVerifyStackedDatasets(
          datasetsStacked.grossNewARRComposition,
          true
        )
      )
    },
    {
      title: 'New Logo Velocity',
      ...processChartData(
        labels,
        yourCompanyVerifyDatasets(datasets.newLogoVelocity, quartileMedian)
      )
    },
    {
      title: 'CAC Payback',
      ...processChartData(
        labels,
        yourCompanyVerifyDatasets(datasets.cacPayback, quartileMedian)
      )
    },
    {
      title: 'OpEx per FTE',
      ...processChartData(
        ChartsARRRangeLabels,
        yourCompanyVerifyDatasets(datasets.opexPerFTE, quartileMedian)
      )
    },
    {
      title: 'Net Dollar Retention',
      ...processChartData(
        labels,
        yourCompanyVerifyDatasets(datasets.netDollarRetention, quartileMedian)
      )
    }
  ];

  const bubbleChartData = [
    {
      title: 'Rule of 40',
      datasets: yourCompanyVerifyDatasets(datasets.ruleOf40, quartileMedian)
    }
  ];

  return {
    barChartData,
    bubbleChartData
  };
};
